import { AltiusRes } from "store/types";

import {
  ICommentChild,
  ICommentParent,
  IGeneralComments,
  IGetCommentsData,
  IPostCommentData,
  IPostCommentResponse,
  IUpdateCommentData
} from "../../types/comments";
import { CommentsAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface ICommentsActions {
  getCommentAction: (data: IGetCommentsData) => Promise<void>;
  createCommentAction: (dealId: number, data: IPostCommentData) => Promise<ICommentParent | ICommentChild>;
  updateCommentAction: (data: IUpdateCommentData) => Promise<void>;
  deleteCommentAction: (id: number, dealId?: number, questionUuid?: string | null) => Promise<void>;
}

interface CommentsActionsParams extends AsyncActionsParams {
  isSharedDeal: boolean;
}

export class CommentsActions extends AsyncActions implements ICommentsActions {
  protected readonly isSharedDeal: boolean;
  constructor({ isSharedDeal, ...params }: CommentsActionsParams) {
    super(params);
    this.isSharedDeal = isSharedDeal;
  }

  public getCommentAction = async (data: IGetCommentsData): Promise<void> => {
    try {
      const { general, formId, dealId } = data;
      this.dispatch({
        type: CommentsAction.GetCommentRequest,
        payload: { general }
      });

      const {
        data: { data: comments }
      } = await this.api.get<AltiusRes<IGeneralComments>>({
        url: this.isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/comments` : "/api/v0.0.3/deals/comments",
        params: {
          form_id: formId,
          general: general
        }
      });
      this.dispatch({
        type: CommentsAction.GetCommentSuccess,
        payload: {
          comments,
          general,
          formId
        }
      });
    } catch (error) {
      this.dispatch({ type: CommentsAction.GetCommentFailure, payload: error });
    }
  };

  public createCommentAction = async (
    dealId: number,
    data: IPostCommentData
  ): Promise<ICommentParent | ICommentChild> => {
    const {
      data: { data: payload },
      status
    } = await this.api.post<IPostCommentResponse>({
      url: this.isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/comments` : "/api/v0.0.3/deals/comments",
      data
    });

    this.dispatch({
      type: CommentsAction.CreateCommentSuccess,
      payload
    });
    return { ...payload, status };
  };

  public updateCommentAction = async (data: IUpdateCommentData): Promise<void> => {
    const { comment_id, text, form_id, deal_id, audience } = data;
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<ICommentParent>>({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${deal_id}/comments/${comment_id}`
        : `/api/v0.0.3/deals/comments/${comment_id}`,
      data: {
        form_id,
        text,
        audience
      }
    });

    this.dispatch({
      type: CommentsAction.UpdateCommentSuccess,
      payload: payload
    });
  };

  public deleteCommentAction = async (id: number, dealId?: number, questionUuid?: string | null): Promise<void> => {
    this.api.delete({
      url: this.isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/comments/${id}` : `/api/v0.0.3/deals/comments/${id}`
    });
    this.dispatch({
      type: CommentsAction.DeleteCommentSuccess,
      payload: { id, questionUuid }
    });
  };
}
